import React, { useState, useEffect, useCallback } from 'react';
import { 
  PlusIcon, 
  MagnifyingGlassIcon, 
  EyeIcon, 
  PencilIcon, 
  TrashIcon,
  DocumentDuplicateIcon,
  ClockIcon,
  TagIcon
} from '@heroicons/react/24/outline';
import templateService, { EmailTemplate, TemplateFilters } from '../../services/templateService';

interface TemplateDashboardProps {
  onCreateTemplate: () => void;
  onEditTemplate: (template: EmailTemplate) => void;
  onPreviewTemplate: (template: EmailTemplate) => void;
}

const TemplateDashboard: React.FC<TemplateDashboardProps> = ({
  onCreateTemplate,
  onEditTemplate,
  onPreviewTemplate
}) => {
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [categories, setCategories] = useState<{ category: string; count: number }[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [total, setTotal] = useState(0);
  
  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [showActiveOnly, setShowActiveOnly] = useState(true);

  const loadTemplates = useCallback(async () => {
    try {
      setLoading(true);
      const filters: TemplateFilters = {
        search: searchTerm || undefined,
        category: selectedCategory || undefined,
        is_active: showActiveOnly ? true : undefined,
        limit: 50,
        offset: 0
      };

      const result = await templateService.getTemplates(filters);
      setTemplates(result.templates);
      setTotal(result.total);
      setError(null);
    } catch (err) {
      console.error('Error loading templates:', err);
      setError(err instanceof Error ? err.message : 'Failed to load templates');
    } finally {
      setLoading(false);
    }
  }, [searchTerm, selectedCategory, showActiveOnly]);

  const loadCategories = useCallback(async () => {
    try {
      const categoriesData = await templateService.getCategories();
      setCategories(categoriesData);
    } catch (err) {
      console.error('Error loading categories:', err);
    }
  }, []);

  // Load templates and categories
  useEffect(() => {
    loadTemplates();
    loadCategories();
  }, [loadTemplates, loadCategories]);

  const handleDeleteTemplate = async (template: EmailTemplate) => {
    if (!window.confirm(`Are you sure you want to delete "${template.name}"? This action cannot be undone.`)) {
      return;
    }

    try {
      await templateService.deleteTemplate(template.id);
      loadTemplates(); // Refresh list
    } catch (err) {
      console.error('Error deleting template:', err);
      alert(err instanceof Error ? err.message : 'Failed to delete template');
    }
  };

  const handleToggleActive = async (template: EmailTemplate) => {
    try {
      await templateService.updateTemplate(template.id, {
        is_active: !template.is_active
      });
      loadTemplates(); // Refresh list
    } catch (err) {
      console.error('Error updating template:', err);
      alert(err instanceof Error ? err.message : 'Failed to update template');
    }
  };

  const getCategoryColor = (category: string) => {
    const colors = {
      newsletter: 'bg-blue-100 text-blue-800',
      welcome: 'bg-green-100 text-green-800',
      resources: 'bg-purple-100 text-purple-800',
      events: 'bg-orange-100 text-orange-800',
      general: 'bg-gray-100 text-gray-800'
    };
    return colors[category as keyof typeof colors] || 'bg-gray-100 text-gray-800';
  };

  const formatDate = (date: Date | string | null) => {
    if (!date) return 'Never';
    return new Date(date).toLocaleDateString();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
        <span className="ml-2 text-gray-600">Loading templates...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Email Templates</h1>
          <p className="text-gray-600">Manage consistent, professional email templates for campaigns</p>
        </div>
        <button
          onClick={onCreateTemplate}
          className="bg-indigo-600 text-white px-4 py-2 rounded-md hover:bg-indigo-700 flex items-center"
        >
          <PlusIcon className="h-5 w-5 mr-2" />
          Create Template
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white p-4 rounded-lg shadow space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Search */}
          <div className="relative">
            <MagnifyingGlassIcon className="h-5 w-5 absolute left-3 top-3 text-gray-400" />
            <input
              type="text"
              placeholder="Search templates..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            />
          </div>

          {/* Category Filter */}
          <div>
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            >
              <option value="">All Categories</option>
              {categories.map(cat => (
                <option key={cat.category} value={cat.category}>
                  {cat.category} ({cat.count})
                </option>
              ))}
            </select>
          </div>

          {/* Active Filter */}
          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              id="activeOnly"
              checked={showActiveOnly}
              onChange={(e) => setShowActiveOnly(e.target.checked)}
              className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
            />
            <label htmlFor="activeOnly" className="text-sm text-gray-700">
              Show active only
            </label>
          </div>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <p className="text-red-700">{error}</p>
        </div>
      )}

      {/* Templates Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
        {templates.map((template) => (
          <div key={template.id} className="bg-white rounded-lg shadow hover:shadow-md transition-shadow">
            <div className="p-6">
              {/* Header */}
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <div className="flex items-center space-x-2 mb-2">
                    <h3 className="text-lg font-semibold text-gray-900 truncate">
                      {template.name}
                    </h3>
                    {!template.is_active && (
                      <span className="px-2 py-1 text-xs bg-gray-100 text-gray-600 rounded">
                        Inactive
                      </span>
                    )}
                  </div>
                  <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getCategoryColor(template.category)}`}>
                    {template.category}
                  </span>
                </div>
                
                {/* Actions Dropdown */}
                <div className="relative group">
                  <button className="p-2 hover:bg-gray-100 rounded-md">
                    <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
                    </svg>
                  </button>
                  
                  <div className="hidden group-hover:block absolute right-0 mt-1 w-48 bg-white rounded-md shadow-lg border z-10">
                    <div className="py-1">
                      <button
                        onClick={() => onPreviewTemplate(template)}
                        className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 w-full text-left"
                      >
                        <EyeIcon className="h-4 w-4 mr-2" />
                        Preview
                      </button>
                      <button
                        onClick={() => onEditTemplate(template)}
                        className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 w-full text-left"
                      >
                        <PencilIcon className="h-4 w-4 mr-2" />
                        Edit
                      </button>
                      <button
                        onClick={() => handleToggleActive(template)}
                        className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 w-full text-left"
                      >
                        <DocumentDuplicateIcon className="h-4 w-4 mr-2" />
                        {template.is_active ? 'Deactivate' : 'Activate'}
                      </button>
                      <button
                        onClick={() => handleDeleteTemplate(template)}
                        className="flex items-center px-4 py-2 text-sm text-red-600 hover:bg-gray-100 w-full text-left"
                      >
                        <TrashIcon className="h-4 w-4 mr-2" />
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Description */}
              <p className="text-gray-600 text-sm mb-4 line-clamp-2">
                {template.description || 'No description provided'}
              </p>

              {/* Segment Tags */}
              {template.segment_tags && template.segment_tags.length > 0 && (
                <div className="mb-4">
                  <div className="flex items-center text-xs text-gray-500 mb-1">
                    <TagIcon className="h-3 w-3 mr-1" />
                    Tags
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {template.segment_tags.slice(0, 3).map((tag, index) => (
                      <span key={index} className="px-2 py-1 text-xs bg-gray-100 text-gray-600 rounded">
                        {tag}
                      </span>
                    ))}
                    {template.segment_tags.length > 3 && (
                      <span className="px-2 py-1 text-xs bg-gray-100 text-gray-500 rounded">
                        +{template.segment_tags.length - 3} more
                      </span>
                    )}
                  </div>
                </div>
              )}

              {/* Stats */}
              <div className="flex items-center justify-between text-xs text-gray-500 pt-4 border-t border-gray-100">
                <div className="flex items-center">
                  <DocumentDuplicateIcon className="h-3 w-3 mr-1" />
                  Used {template.usage_count} times
                </div>
                <div className="flex items-center">
                  <ClockIcon className="h-3 w-3 mr-1" />
                  {formatDate(template.last_used_at)}
                </div>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="px-6 py-3 bg-gray-50 rounded-b-lg">
              <div className="flex space-x-2">
                <button
                  onClick={() => onPreviewTemplate(template)}
                  className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-100 text-center"
                >
                  Preview
                </button>
                <button
                  onClick={() => onEditTemplate(template)}
                  className="flex-1 px-3 py-2 text-sm bg-indigo-600 text-white rounded-md hover:bg-indigo-700 text-center"
                >
                  Edit
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Empty State */}
      {templates.length === 0 && !loading && (
        <div className="text-center py-12">
          <div className="text-gray-400 text-6xl mb-4">📧</div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">No templates found</h3>
          <p className="text-gray-600 mb-6">
            {searchTerm || selectedCategory ? 
              'Try adjusting your filters to find templates.' : 
              'Get started by creating your first email template.'
            }
          </p>
          <button
            onClick={onCreateTemplate}
            className="bg-indigo-600 text-white px-4 py-2 rounded-md hover:bg-indigo-700"
          >
            Create Your First Template
          </button>
        </div>
      )}

      {/* Results Summary */}
      {templates.length > 0 && (
        <div className="text-center text-sm text-gray-600">
          Showing {templates.length} of {total} templates
        </div>
      )}
    </div>
  );
};

export default TemplateDashboard;